import { FAQCategory } from './FAQSection';

export const faqData: FAQCategory[] = [
	{
		slug: 'general',
		title: 'General',
		content: [
			{
				question: 'What is Simsola AI?',
				answer:
					'Simsola AI builds AI-powered tools for the parts of talent development that happen after a resume gets a look — the “last mile” of hiring and coaching. We offer two products: the Assessment Service, which scores candidate interviews for recruiting teams, and My Coach, a learning and development platform for simulation-based coaching.'
			},
			{
				question: 'What products does Simsola offer?',
				answer:
					'Two: the Assessment Service, a B2B tool that helps recruiting teams evaluate candidates from interview transcripts, and My Coach, an L&D platform that lets coaches run simulation-based training programs for cohorts. Theyre built on the same underlying assessment technology but serve different use cases and audiences.'
			},
			{
				question: 'Who uses Simsola AI?',
				answer:
					'Recruiting teams and talent acquisition leaders use the Assessment Service to evaluate candidates. Coaches, trainers, and program leads use My Coach to run structured development programs for cohorts of learners.'
			},
			{
				question: 'How do I get started?',
				answer:
					'It depends on the product — visit the Assessment Service or My Coach pages for product-specific sign-up details, or use “Start a Free Trial” above.'
			},
			{
				question: 'Do you offer a free trial?',
				answer:
					"Yes. When you sign up for the assessment service, you'll receive a one-week free trial that includes up to five interview analyses."
			},
			{
				question: 'How is Simsola different from other AI hiring or coaching tools?',
				answer:
					'Simsola is built around the parts of the process that are usually manual and inconsistent — assessing what happened in an actual conversation, not just parsing a resume or job description. Scoring is grounded in structured interview data rather than keyword matching.'
			},
			{
				question: 'Is my data handled securely?',
				answer:
					'Yes — see the security and data-handling answers above for details on encryption, audits, and data control.'
			},
			{
				question: 'How do I contact support?',
				answer: 'Email service@simsola.ai. Or fill in the contact us form.'
			},
			{
				question: 'Where can I find your Terms of Service and Privacy Policy?',
				answer: 'They are located at the sign up page for each service.'
			}
		]
	},
	{
		slug: 'product',
		title: 'Product',
		content: [
			{
				question: "What's the difference between the Assessment Service and My Coach?",
				answer:
					"The Assessment Service is built for recruiting teams: it scores candidates from interview transcripts to support hiring decisions. My Coach is built for learning and development: it lets Cohort Members practice through simulations and gives Coaches visibility into their progress. Both run on Simsola's underlying assessment engine, but the workflows, personas, and outputs are distinct."
			},
			{
				question: 'Can I use both products together?',
				answer:
					"They're separate products with separate accounts today. If your organization uses both for different purposes (hiring and internal development, for example), reach out to your account contact to discuss."
			},
			{
				question: 'Which product is right for my team?',
				answer:
					"If you're evaluating external candidates during a hiring process, the Assessment Service is the fit. If you're developing the skills of an existing team, fellowship, or cohort over time, My Coach is the fit."
			},
			{
				question: "How does Simsola's scoring technology actually work?",
				answer:
					'Simsola analyzes structured conversation data (an interview or simulation transcript) against a set of defined dimensions — things like communication, critical thinking, and work style — and produces a score, an archetype, and a written breakdown of strengths and areas for growth. The same underlying engine powers both products.'
			},
			{
				question: 'Does Simsola replace human judgment in hiring or coaching decisions?',
				answer:
					"No. Simsola produces structured, consistent data to inform decisions — it doesn't make hiring decisions or replace a recruiter's or coach's judgment."
			},
			{
				question: 'What integrations does Simsola support?',
				answer:
					'The Assessment Service integrates with recruiting platforms including Greenhouse, Ashby, Ezekia, and ClockWork Recruiting, so scores can be delivered directly back to the candidate record without leaving your existing tools.'
			},
			{
				question: 'Is there an API?',
				answer:
					'Yes — the Assessment Service is available as an API, in addition to the dashboard, so you can build assessment scoring directly into your own workflows.'
			}
		]
	},
	{
		slug: 'my-coach',
		title: 'My Coach',
		content: [
			{
				question: 'What is My Coach?',
				answer:
					"My Coach is Simsola's learning and development platform. It lets Cohort Members train through realistic simulations for professional or educational scenarios, and gives Coaches tools to track and support their progress."
			},
			{
				question: 'Who is My Coach for?',
				answer:
					'Two groups: Coaches, who manage a cohort, set what members can access, and monitor progress; and Cohort Members, who complete simulations and training activities.'
			},
			{
				question: "What's the difference between a Coach and a Cohort Member account?",
				answer:
					'Coaches see a dashboard view across their entire cohort — participant progress, simulation activity, and assessment summaries. Cohort Members see their own individual activity and results, including simulation scores and meeting analysis.'
			},
			{
				question: 'What kinds of simulations can Cohort Members practice?',
				answer:
					'Simulations are designed around real-world professional or educational scenarios relevant to the program a Cohort Member is enrolled in — for example, leadership or coaching-style conversations.'
			},
			{
				question: 'Can Cohort Members upload their own recordings for feedback?',
				answer:
					'Yes — through the “Analyze Meetings” feature, Cohort Members can upload a meeting or simulation transcript and receive a rubric-based score breakdown, including a summary and per-attribute scoring for the relevant coaching level.'
			},
			{
				question: "Can Coaches see the detailed results behind a Cohort Member's score?",
				answer:
					"Yes — Coaches can view score breakdowns for a Cohort Member's simulation and transcript uploads, including the rubric level used and component-level scores."
			},
			{
				question: 'Can Coaches track progress across an entire cohort at once?',
				answer:
					"Yes — the Coach dashboard shows simulation activity, summaries, and assessment activity across all Cohort Members, so Coaches don't need to check each participant individually."
			},
			{
				question: 'How do I get access to My Coach?',
				answer:
					"My Coach access is typically provisioned through your organization's program or cohort. Check with your program administrator or Coach for an invitation."
			},
			{
				question: 'Is My Coach used for professional development, education, or both?',
				answer:
					'Both — My Coach is used across professional development programs (like leadership fellowships) and educational training contexts.'
			}
		]
	},
	{
		slug: 'assessment-service-for-businesses',
		title: 'Assessment Service for Businesses',
		content: [
			{
				question: 'What is the Assessment Service?',
				answer:
					"The Assessment Service is an API-based candidate assessment tool for recruiting teams. Upload an interview transcript, and Simsola's AI engine analyzes it against a defined set of leadership dimensions to produce a structured candidate score, archetype, and breakdown of strengths and areas for growth."
			},
			{
				question: 'Who is it for?',
				answer:
					'Two roles: Client Admins, who manage the account, subscription, and team; and Recruiters, who upload transcripts, create jobs, and review candidate scores day to day.'
			},
			{
				question: 'How does candidate scoring work?',
				answer:
					"Every candidate is scored against the same fixed set of leadership dimensions (for example, communication, critical thinking, and work style) — scores are candidate-level and consistent across every candidate, not customized per job description. When comparing candidates for a specific role, recruiters can assign relative importance to each dimension to produce a job-specific ranking for that comparison — but the underlying candidate scores themselves don't change."
			},
			{
				question: 'What do I need to run an assessment?',
				answer: 'An interview transcript. Simsola supports PDF, DOCX, SRT, JSON, MP4, TXT, and VTT formats.'
			},
			{
				question: 'Can I compare multiple candidates against each other?',
				answer:
					'Yes — the Compare View lets you select candidates for a job and see them stack-ranked side by side across every scoring dimension.'
			},
			{
				question: 'Does Simsola integrate with my existing ATS?',
				answer:
					"Yes — Simsola integrates with Greenhouse, Ashby, Ezekia, and ClockWork Recruiting. Once connected, transcripts uploaded in your ATS are automatically scored and the results are written back as a candidate note — no need to log into Simsola separately. We can integrate into your organization's platform as well, contact us for details."
			},
			{
				question: 'How much does the Assessment Service cost?',
				answer:
					'The Base plan is $150/month and includes 20 credits. If you use more than your monthly allotment, additional credits are available in a $50 bundle (5 credits). Enterprise pricing is available for teams with higher volume or custom needs — contact sales for a quote.'
			},
			{
				question: 'What happens if I run out of credits?',
				answer:
					'You can enable automatic overage bundle purchases so scoring continues without interruption, or purchase a bundle manually from your Billing page. If neither is set up, new assessments are paused until credits are added.'
			},
			{
				question: 'Can multiple recruiters use one account?',
				answer:
					'Yes — Recruiter seats are unlimited and not billed separately; your plan is based on credit usage, not number of teammates.'
			},
			{
				question: 'Do I need an active subscription to invite teammates or create jobs?',
				answer:
					"No — inviting recruiters, creating jobs, and creating candidate records don't require an active subscription. A subscription is only required for transcript uploads, assessment processing, and API key creation."
			},
			{
				question: 'What happens if I upload a candidate who already exists?',
				answer:
					'Simsola checks for matching name and email within your company account and will flag likely duplicates so you can confirm before proceeding, rather than blocking the upload outright.'
			},
			{
				question: 'Is there an API I can build directly into our own systems?',
				answer:
					'Yes — the Assessment Service is available as a direct API for teams that want to build assessment scoring into internal tools or a custom workflow, in addition to the dashboard.'
			},
			{
				question: 'Is there an enterprise plan?',
				answer:
					'Yes — Enterprise includes custom pricing, white-label options, and direct API access for teams with high-volume or bespoke needs. Contact sales to discuss.'
			}
		]
	},
	{
		slug: 'assessment-service-for-candidates',
		title: 'Assessment Service for Candidates',
		callout:
			"Note: the Assessment Service for Candidates is a standalone app — a candidate's account is their own and isn't associated with, or visible to, any company they've interviewed with. This section still touches on AI scoring and data-use topics, so recommend a Legal/Compliance pass before publishing, particularly on the bias/fairness answer.",
		content: [
			{
				question: 'What is the Assessment Service for Candidates?',
				answer:
					"It's a free-standing tool you use on your own, separate from any company you're interviewing with. After you've had a job interview, you can upload your interview transcript and get a detailed, structured breakdown of what you did well and where you can improve — so you walk into your next interview stronger."
			},
			{
				question: 'Do I need my own account? Is it connected to the company I interviewed with?',
				answer:
					"Yes, you create your own independent Simsola account, and no — it isn't tied to, or visible to, any employer. What you upload and what you do with your results is entirely up to you."
			},
			{
				question: 'Where do I get a transcript of my interview to upload?',
				answer:
					'Typical source(s) candidates use a recording/transcript you already have from the interview, a call-recording tool such as Granola, Zoom, Teams, etc..'
			},
			{
				question: 'What happens to my interview transcript once I upload it?',
				answer:
					"It's submitted to Simsola's assessment engine, analyzed against a set of defined evaluation dimensions, and the results are added to your account for you to review."
			},
			{
				question: 'How am I scored?',
				answer:
					'Your interview is evaluated against the same set of dimensions Simsola uses consistently across all candidates — such as communication, critical thinking, and work style — so you get an objective, consistent read on your interview performance rather than a subjective one.'
			},
			{
				question: 'Is a person involved, or is this fully automated?',
				answer:
					"Your score and written breakdown are generated automatically by Simsola's assessment engine."
			},
			{
				question: 'Who can see my results?',
				answer:
					"Only you. Your account isn't connected to any employer, so your results aren't shared with anyone automatically — it's your choice whether and how to share them."
			},
			{
				question: 'Can I request that my data be deleted?',
				answer:
					'Yes. Candidates hold their own accounts.'
			},
			{
				question: 'Do I get to see my own results?',
				answer:
					'Yes — your full score, strengths, and areas for growth are available directly in your account as soon as your assessment is processed.'
			},
			{
				question: "Does a low score mean I won't get the job?",
				answer:
					"No — this tool isn't part of any employer's hiring decision or visible to them at all. It's meant to help you understand your own interview performance so you can improve for future opportunities."
			},
			{
				question: 'Is there a cost to use the Assessment Service for Candidates?',
				answer:
					'yes, coming soon.'
			},
			{
				question: 'How is my data protected?',
				answer:
					'Point to the same data protection standards referenced in the General FAQ, once confirmed applicable to candidate accounts specifically.'
			}
		]
	},
	{
		slug: 'ezekia',
		title: 'Ezekia',
		content: [
			{
				question: 'Does Simsola work with Ezekia?',
				answer:
					"Yes. Simsola connects to Ezekia so that when a recruiter uploads an interview transcript to a candidate, it's scored automatically and the results are posted back to that candidate as a note — scoped to the right assignment. A Client Admin sets the connection up once."
			},
			{
				question: 'What do I need before connecting Ezekia?',
				answer:
					"An active Simsola subscription, and an Ezekia API bearer token. You'll generate the token in Ezekia under Settings → Integrations → API Tokens → Generate New Token. Treat it like a password — Ezekia shows it only once."
			},
			{
				question: 'How do I connect Ezekia to Simsola?',
				answer:
					'In Simsola, open Integrations, click Connect Ezekia, and paste in your bearer token. Simsola validates it right away and flags any issue inline. Then, in Ezekia, add a webhook subscription under Settings → Integrations → Webhooks pointing at the receiver URL shown in the Simsola setup screen.'
			},
			{
				question: "How do I know it's working?",
				answer:
					'Once connected, the Ezekia tile in Simsola shows an active status. From then on, uploading a transcript to a candidate in Ezekia triggers scoring automatically, and a Simsola note appears on that candidate\'s profile. If a transcript doesn\'t have enough to score, Simsola posts a short "insufficient data" note instead.'
			},
			{
				question: 'What if my token stops working?',
				answer:
					"Ezekia tokens expire on a schedule set when they're created, and Ezekia sends renewal reminders before they do. When a token expires or is revoked, reconnect from the Ezekia tile in Simsola with a fresh token."
			}
		]
	},
	{
		slug: 'greenhouse',
		title: 'Greenhouse',
		content: [
			{
				question: 'Does Simsola work with Greenhouse?',
				answer:
					"Yes. Recruiters can trigger a Simsola assessment without leaving Greenhouse — just upload an interview transcript to the candidate, and Simsola scores it and posts the results back as a note on the candidate's profile. A Client Admin connects the account once."
			},
			{
				question: 'What do I need before connecting Greenhouse?',
				answer:
					"An active Simsola subscription and a Greenhouse Harvest API key. You'll create the key in Greenhouse under Configure → Dev Center → API Credential Management."
			},
			{
				question: 'How do I connect Greenhouse to Simsola?',
				answer:
					'In Simsola, open Integrations, click Connect Greenhouse, and paste in your Harvest API key. Simsola validates it and confirms the connection. There are no webhooks to configure — once the key is saved, Simsola begins watching for new transcripts automatically.'
			},
			{
				question: 'How does a recruiter run an assessment?',
				answer:
					"Open the candidate's Application tab in Greenhouse and upload the interview transcript as a supported file (.txt, .pdf, or .docx). Simsola detects the upload, scores it, and posts the result as a note on the candidate's profile — no need to log into Simsola. Very short or unsupported files are skipped."
			},
			{
				question: "How do I know it's working?",
				answer:
					'The Greenhouse tile in Simsola shows the connection status and when it last detected an upload or posted a note, so you can confirm it\'s running without contacting support. If a transcript can\'t be scored, Simsola posts a brief "insufficient data" note instead of a score.'
			}
		]
	},
	{
		slug: 'clockwork',
		title: 'ClockWork',
		content: [
			{
				question: 'Does Simsola work with ClockWork Recruiting?',
				answer:
					'Yes. Simsola connects to ClockWork so interview transcripts on your candidates are scored automatically and the results are posted back as notes — without anyone logging into Simsola. A Client Admin sets it up once for the firm.'
			},
			{
				question: 'What do I need before connecting ClockWork?',
				answer:
					"An active Simsola subscription and four credentials from your ClockWork admin settings: your firm slug, API key, API secret, and firm service key. A quick note: connecting ClockWork requires an active subscription — if you don't have one yet, Simsola will prompt you to choose a plan first."
			},
			{
				question: 'How do I connect ClockWork to Simsola?',
				answer:
					"In Simsola, open Integrations and click Connect ClockWork. Enter all four credentials in the modal — the setup screen shows where to find each one in ClockWork. Simsola verifies them against ClockWork before marking the connection active, so you'll know right away if something's off."
			},
			{
				question: "How do I know it's working?",
				answer:
					"Once verified, the ClockWork tile shows an active status, along with the last successful check and the last note delivered, so you have passive visibility into whether it's running. From there, scoring happens automatically as transcripts appear on your candidates."
			},
			{
				question: 'How do I update or remove the connection?',
				answer:
					"From the ClockWork tile in Simsola you can rotate your credentials, re-verify the connection if you suspect an issue, or disable/delete it entirely. Rotating verifies the new credentials before switching over, so there's no downtime."
			}
		]
	},
	{
		slug: 'ashby',
		title: 'Ashby',
		content: [
			{
				question: 'Does Simsola work with Ashby?',
				answer:
					"Yes. Simsola connects to Ashby to automatically score interview transcripts — from both Ashby's AI Notetaker and manually uploaded files — and posts the results back to the candidate's profile as a note. Recruiters keep working in Ashby; a Client Admin sets the connection up once."
			},
			{
				question: 'What do I need before connecting Ashby?',
				answer:
					"An active Simsola subscription, and Ashby access with permission to create an API key and configure webhooks. If you want to score AI Notetaker transcripts, you'll also need the AI Notetaker add-on active in Ashby."
			},
			{
				question: 'How do I connect Ashby to Simsola?',
				answer:
					"In Ashby, go to Admin → Integrations → API Keys and create a key with the scopes shown in the Simsola setup screen. Then, in Simsola, open Integrations, click Connect Ashby, and paste the key in. Simsola checks the key right away and tells you inline if anything's missing."
			},
			{
				question: 'Do AI Notetaker and manual uploads need different setup?',
				answer:
					'A little. AI Notetaker transcripts work as soon as you connect — nothing extra to configure. For manually uploaded transcripts, you\'ll tell Simsola which interview stage(s) mean "ready for scoring" during setup, so it knows when to look for a file. If your team only uses the Notetaker, you can skip that step.'
			},
			{
				question: 'How do I set up the Ashby webhooks?',
				answer:
					"In Ashby, go to Admin → Integrations → Webhooks and add two subscriptions pointing at the receiver URL shown in Simsola's Connect Ashby screen. Adding both means you're covered whether a transcript comes from the Notetaker or a manual upload."
			},
			{
				question: "How do I know it's working?",
				answer:
					'Once connected, the Ashby tile in Simsola shows a Connected status, and scoring starts automatically. After an interview finishes or a transcript is uploaded at a mapped stage, a Simsola score note appears on the candidate\'s Ashby profile. If a transcript doesn\'t have enough to score, Simsola posts a short "insufficient data" note instead.'
			},
			{
				question: 'What if scoring stops or my interview plans change?',
				answer:
					'If your Ashby API key is rotated or revoked, Simsola shows an Update API Key prompt on the Ashby integration card — just paste in a new key. And if you restructure your interview stages, revisit your stage mapping in Simsola so it keeps looking in the right place.'
			}
		]
	},
	{
		slug: 'ai-assistant',
		title: 'AI Assistant',
		content: [
			{
				question: 'What is an MCP server, and why do I need one?',
				answer:
					"MCP (Model Context Protocol) is an open standard for connecting AI assistants to outside systems. The Simsola MCP Server is what lets your assistant securely reach your assessment data — and it ships with the tools and context the model needs to interpret that data correctly, rather than guessing at it."
			},
			{
				question: 'Which AI assistants does it work with?',
				answer:
					'Claude (Anthropic), ChatGPT (OpenAI), Copilot (Microsoft) and any other assistant or agent platform that supports MCP connectors.'
			},
			{
				question: 'How hard is the set-up?',
				answer:
					"Straightforward. Add the Simsola connector in your AI platform's connector settings, authorize with your Simsola account, and you're connected. Most teams are running their first query the same day."
			},
			{
				question: 'Does everyone on my team have to use the same AI assistant?',
				answer:
					'No. If some people prefer Claude and others prefer ChatGPT, the connector works with both — everyone reads from the same Simsola data.'
			},
			{
				question: 'Do I need a paid AI subscription?',
				answer:
					"Usually yes. Most AI assistants require a paid plan to use MCP connectors. Check your provider's current plan details."
			},
			{
				question: 'Is our data secure?',
				answer:
					"Access is authenticated and permissioned — through your AI assistant, a user can only reach what they're already entitled to see in Simsola. Simsola is SOC 2 Type 2 certified. Ask us for our current security documentation."
			},
			{
				question: 'Does the assistant see candidate identities?',
				answer:
					"Simsola's scoring works from the conversation itself — the assessment layer is designed to evaluate what was said, not who said it. We're happy to walk your team through how we handle identity and bias in the scoring pipeline."
			},
			{
				question: 'Does the MCP connector cost extra?',
				answer: "The MCP connector is included with your Simsola subscription. Reach out and we'll confirm what's covered under your plan."
			}
		]
	}
];
